import { FC, useContext } from "react";
import { Controller } from "react-hook-form";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import { AsyncSelect, Form, FormField, ModalDialog } from "@rothoblaas/design-system";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useAsyncOptions from "../../../../hooks/useAsyncOptions";
import useI18n from "../../../../hooks/useTranslations";
import {
  InsertScrewFamilyBody,
  ScrewFamily,
  UpdateScrewFamilyBody,
} from "../../../../http/deckDesigner/types/screwFamilies";
import { SubstructureMaterial } from "../../../../http/deckDesigner/types/substructureMaterials";
import useDeckScrewFamiliesApi from "../../../../http/deckDesigner/useScrewFamilies";
import useDeckSubstructureMaterialsApi from "../../../../http/deckDesigner/useSubstructureMaterials";
import TranslatableTextField from "../../../../components/ui/TranslatableTextField";

interface ScrewFamilyFormBody {
  name: string | null;
  nameIt: string | null;
  nameDe: string | null;
  nameEs: string | null;
  nameFr: string | null;
  substructureMaterials: SubstructureMaterial[];
}

interface ScrewFamilyFormProps {
  screwFamily?: ScrewFamily;
  onClose: () => void;
}

const ScrewFamilyForm: FC<ScrewFamilyFormProps> = ({ screwFamily, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const screwFamiliesApi = useDeckScrewFamiliesApi();
  const substructureMaterialsApi = useDeckSubstructureMaterialsApi();
  const substructureMaterialOptions = useAsyncOptions(substructureMaterialsApi.getDeckSubstructureMaterials);

  const defaultValues: ScrewFamilyFormBody = {
    name: screwFamily?.name ?? null,
    nameIt: screwFamily?.nameIt ?? null,
    nameDe: screwFamily?.nameDe ?? null,
    nameEs: screwFamily?.nameEs ?? null,
    nameFr: screwFamily?.nameFr ?? null,
    substructureMaterials: screwFamily?.substructureMaterials ?? [],
  };

  const onSubmit = () => async (formData: ScrewFamilyFormBody) => {
    if (!formData.substructureMaterials.length) return;

    const body: InsertScrewFamilyBody | UpdateScrewFamilyBody = {
      name: formData.name ?? "",
      nameIt: formData.nameIt ?? "",
      nameDe: formData.nameDe ?? "",
      nameEs: formData.nameEs ?? "",
      nameFr: formData.nameFr ?? "",
      substructureMaterials: formData.substructureMaterials,
    };

    try {
      if (screwFamily) {
        await screwFamiliesApi.updateDeckScrewFamily(screwFamily.id, body);
      } else {
        await screwFamiliesApi.insertDeckScrewFamily(body);
      }
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={
        screwFamily
          ? `${i18n.translation.common.edit} ${i18n.translation.deckDesignerModule.screwFamilies.singular}`
          : `${i18n.translation.common.add} ${i18n.translation.deckDesignerModule.screwFamilies.singular}`
      }
    >
      <Form<ScrewFamilyFormBody>
        submitText={i18n.translation.common.save}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {(form) => (
          <FormFieldsContainer fullWidth>
            <TranslatableTextField form={form} />
            <Controller
              name="substructureMaterials"
              control={form.control}
              rules={{ validate: (value) => !!value.length }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.deckDesignerModule.substructureMaterials.plural}
                  error={fieldState.error}
                >
                  {({ labelId }) => (
                    <AsyncSelect<SubstructureMaterial, true>
                      {...substructureMaterialOptions}
                      inputId={labelId}
                      value={field.value}
                      isMulti={true}
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.code}
                      isInvalid={fieldState.invalid}
                      onChange={(options) => field.onChange(Array.from(options))}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        )}
      </Form>
    </ModalDialog>
  );
};

export default ScrewFamilyForm;
